<template>
  <!--  the bar-->
  <!--  Add padding to div below-->
  <top-navigation
    :title="$t('pages.quotes.edit')"
    :disabled="!validated || status.deleting"
    :loading="status.updating"
    :button-name="$t('global.inputs.labels.update')"
    @submit-click="update()"
  />
  <div class="mx-auto min-h-screen pt-24">
    <div class="mb-4 w-full">
      <text-area-input
        v-model="quote"
        :has-error="validationErrors.quote"
        :error-message="$t('global.inputs.errors.quote_mandatory')"
        :label="$t('global.inputs.labels.quote') + '*'"
        :placeholder="$t('global.inputs.placeholders.quote')"
      />
    </div>

    <div class="mb-4 w-full">
      <p class="font-medium dark:text-slate-200">
        {{ $t("pages.quotes.who_said_it") }}
      </p>
      <p v-if="validationErrors.authors" class="mt-1 text-sm text-red-600">
        {{ $t("pages.quotes.one_author_minimum") }}
      </p>
      <multi-selector
        v-model="authors"
        label-key="name"
        index-key="uuid"
        :options="groupMemberOptions"
      />
    </div>
    <div class="mb-4 w-full">
      <text-input
        v-model="customAuthor"
        :label="$t('global.inputs.labels.quote_custom_author')"
        :placeholder="$t('global.inputs.placeholders.quote_custom_author')"
      />
    </div>
    <div class="mb-4 w-full">
      <text-area-input
        v-model="context"
        :has-error="validationErrors.description"
        error-message=""
        :label="$t('global.inputs.labels.quote_additional_context')"
        :placeholder="$t('global.inputs.placeholders.quote_additional_context')"
      />
    </div>

    <div class="mb-4 w-full">
      <p class="font-medium dark:text-slate-200">
        {{ $t("global.inputs.labels.date_time") }} *
      </p>
      <p v-show="validationErrors.timestamp" class="mt-1 text-sm text-red-600">
        {{ $t("global.inputs.errors.date_time") }}
      </p>
      <div class="flex w-full flex-col items-center">
        <date-input v-model="timestamp" :date-time="true"></date-input>
      </div>
    </div>

    <div class="mb-4 w-full">
      <default-button
        color="red"
        :disabled="status.updating"
        :loading="status.deleting"
        @click="openDeleteConformation = true"
        ><p>
          {{ $t("global.inputs.labels.delete_quote") }}
        </p></default-button
      >
    </div>
  </div>
  <conformation-modal
    :danger="true"
    :confirm-text="$t('pages.quotes.confirm_delete')"
    :loading="status.deleting"
    :cancel-text="$t('pages.quotes.confirm_close')"
    :visible="openDeleteConformation"
    @cancel="openDeleteConformation = false"
    @confirm="this.delete()"
  >
    <p class="my-8 text-center font-medium">
      {{ $t("pages.quotes.confirm_delete_text") }}
    </p>
  </conformation-modal>
</template>
<script>
import { firestore } from "@/helpers/firebase/firebase";
import {
  collection,
  updateDoc,
  increment,
  writeBatch,
  doc,
} from "firebase/firestore";
import {
  GET_GROUP_DOC_ID,
  GET_GROUP_MEMBERS_SORTED,
} from "@/store/modules/groups";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import TopNavigation from "../components/TopNavigation.vue";
import TextInput from "@/components/Inputs/TextInput.vue";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import { GET_GROUP_DATA } from "@/store/modules/groups";
import MultiSelector from "../components/Inputs/MultiSelector.vue";
import DefaultButton from "../components/Buttons/DefaultButton.vue";
import ConformationModal from "@/components/Modals/ConformationModal.vue";
import { globals } from "@/helpers/globals/globals";
import DateInput from "@/components/Inputs/DateInput.vue";
import { dateObjectToDateString } from "@/helpers/converts/date-converter";
import {
  GET_SELECTED_QUOTE,
  GET_SELECTED_QUOTE_DATA,
  HAS_SELECTED_QUOTE,
} from "@/store/modules/quotes";
import { GET_ACTIVE_GROUP } from "@/store/modules/user";

export default {
  name: "EditQuoteView",
  components: {
    DateInput,
    ConformationModal,
    MultiSelector,
    TextAreaInput,
    TextInput,
    TopNavigation,
    DefaultButton,
  },
  data() {
    return {
      status: {
        updating: false,
        deleting: false,
      },
      context: null,
      quote: null,
      timestamp: null,
      customAuthor: null,
      authors: [],
      openDeleteConformation: false,
    };
  },
  computed: {
    darkModeEnabled() {
      return !!globals.darkModeEnabled;
    },
    groupData() {
      return this.$store.getters[GET_GROUP_DATA];
    },
    groupMembers() {
      return this.groupData.members ? this.groupData.members : {};
    },
    groupMembersSorted() {
      return this.$store.getters[GET_GROUP_MEMBERS_SORTED];
    },
    groupMemberOptions() {
      let options = [];
      for (const [key, value] of Object.entries(this.groupMembers)) {
        let memberObject = {
          uuid: key,
          name: value.name ? value.name : this.$t("global.deleted_user"),
        };
        options.push(memberObject);
      }
      return options;
    },
    timestampValid() {
      return this.timestamp;
    },
    quoteValid() {
      return this.quote && this.quote.length > 0 && this.quote.length < 350;
    },
    contextValid() {
      if (this.context) {
        return this.context.length < 350;
      } else {
        return true;
      }
    },
    authorsValid() {
      if (this.authors.length === 0 && !this.customAuthor) {
        return false;
      } else if (this.authors.length === 0 && this.customAuthor) {
        return this.customAuthor.length > 1 && this.customAuthor.length < 350;
      }
      return true;
    },
    validationErrors() {
      let errors = {};
      errors.quote = !this.quoteValid;
      errors.context = !this.contextValid;
      errors.timestamp = !this.timestampValid;
      errors.authors = !this.authorsValid;
      return errors;
    },
    validated() {
      return !Object.values(this.validationErrors).some(
        (value) => value === true,
      );
    },
  },
  created() {
    if (!this.$store.getters[HAS_SELECTED_QUOTE]) {
      this.$router.push({ name: "Quotes" });
    } else {
      this.context = this.$store.getters[GET_SELECTED_QUOTE_DATA].context;
      this.quote = this.$store.getters[GET_SELECTED_QUOTE_DATA].quote;
      this.timestamp = dateObjectToDateString(
        this.$store.getters[GET_SELECTED_QUOTE_DATA].timestamp.toDate(),
        "yyyy-mm-ddThh:mm",
      );

      this.customAuthor =
        this.$store.getters[GET_SELECTED_QUOTE_DATA].custom_author;
      this.authors = this.$store.getters[GET_SELECTED_QUOTE_DATA].authors;
    }
  },
  methods: {
    async update() {
      this.status.updating = true;

      if (!this.validated) {
        setWarningToast({
          message: this.$t("global.toast.check_all_fields"),
        });
        return;
      }

      let trigrams = {};
      let trimmedQuote = this.quote;
      trimmedQuote = trimmedQuote.replaceAll(" ", "").toLowerCase();
      if (trimmedQuote.length >= 3) {
        for (let i = 0; i < trimmedQuote.length - 2; i++) {
          let trigram = trimmedQuote.slice(i, i + 3);
          trigrams[trigram] = true;
        }
      }

      try {
        let quoteCollection = collection(
          firestore,
          "groups/" + this.$store.getters[GET_ACTIVE_GROUP] + "/quotes",
        );
        await updateDoc(
          doc(quoteCollection, this.$store.getters[GET_SELECTED_QUOTE].id),
          {
            quote: this.quote,
            timestamp: new Date(this.timestamp),
            authors: this.authors ? this.authors : [],
            custom_author: this.customAuthor ? this.customAuthor : "",
            context: this.context ? this.context : "",
            trigrams: trigrams,
          },
        );
        setSuccessToast({
          message: this.$t("global.inputs.labels.quote_edited"),
        });
        this.$router.push({ name: "Quotes" });
      } catch (e) {
        handleFirestoreException({ e: e });
      } finally {
        this.status.updating = false;
      }
    },
    async delete() {
      try {
        this.status.deleting = true;

        let quotesCollection = collection(
          firestore,
          "groups/" + this.$store.getters[GET_ACTIVE_GROUP] + "/quotes",
        );
        let quoteRef = doc(
          quotesCollection,
          this.$store.getters[GET_SELECTED_QUOTE].id,
        );
        let groupCollection = collection(firestore, "groups/");
        let groupRef = doc(
          groupCollection,
          this.$store.getters[GET_GROUP_DOC_ID],
        );
        let batch = writeBatch(firestore);

        batch.delete(quoteRef);
        batch.update(groupRef, {
          "general_data.number_of_quotes": increment(-1),
        });
        await batch.commit();
        setSuccessToast({
          message: this.$t("global.toast.deleted_successfully"),
        });
        this.$router.push({ name: "Quotes" });
      } catch (e) {
        handleFirestoreException({ e: e });
      } finally {
        this.status.deleting = false;
      }
    },
  },
};
</script>
